import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  Avatar,
  Stack,
  Divider,
  InputAdornment,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Switch,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  VideoCall as VideoCallIcon,
  Event as EventIcon,
  Link as LinkIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  Description as DocumentIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import HtmlEditor from "./HtmlEditor";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

export default function CreateEditMeetingModal({
  open,
  onClose,
  onSave,
  meeting = null,
  employees = [],
  loading = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isEdit = Boolean(meeting && meeting.id);

  const [title, setTitle] = useState("");
  const [meetingDatetime, setMeetingDatetime] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [selectedParticipantIds, setSelectedParticipantIds] = useState([]);
  const [searchEmployee, setSearchEmployee] = useState("");

  // Document preparation
  const [includeDocument, setIncludeDocument] = useState(false);
  const [documentTitle, setDocumentTitle] = useState("");
  const [documentContent, setDocumentContent] = useState("");

  useEffect(() => {
    if (meeting) {
      setTitle(meeting.title || "");
      if (meeting.meeting_datetime) {
        const d = new Date(meeting.meeting_datetime);
        const pad = (n) => String(n).padStart(2, "0");
        const formatted = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        setMeetingDatetime(formatted);
      } else {
        setMeetingDatetime("");
      }
      setMeetingLink(meeting.meeting_link || "");
      setSelectedParticipantIds(
        (meeting.participants || []).map((p) => p.employee_id)
      );

      if (meeting.document_id || meeting.document_title || meeting.document_content) {
        setIncludeDocument(true);
        setDocumentTitle(meeting.document_title || `${meeting.title} - Notes`);
        setDocumentContent(meeting.document_content || "");
      } else {
        setIncludeDocument(false);
        setDocumentTitle("");
        setDocumentContent("");
      }
    } else {
      const now = new Date();
      now.setHours(now.getHours() + 1, 0, 0, 0);
      const pad = (n) => String(n).padStart(2, "0");
      const formatted = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;

      setTitle("");
      setMeetingDatetime(formatted);
      setMeetingLink("");
      setSelectedParticipantIds([]);
      setIncludeDocument(false);
      setDocumentTitle("");
      setDocumentContent("");
    }
  }, [meeting, open]);

  const handleToggleParticipant = (empId) => {
    setSelectedParticipantIds((prev) =>
      prev.includes(empId) ? prev.filter((id) => id !== empId) : [...prev, empId]
    );
  };

  const handleSelectAll = () => {
    setSelectedParticipantIds(employees.map((e) => e.id));
  };

  const handleClearAll = () => {
    setSelectedParticipantIds([]);
  };

  const filteredEmployees = employees.filter((emp) => {
    if (!searchEmployee.trim()) return true;
    const term = searchEmployee.toLowerCase();
    const name = `${emp.first_name || ""} ${emp.last_name || ""}`.toLowerCase();
    const email = (emp.email || "").toLowerCase();
    const dept = (emp.department_name || "").toLowerCase();
    return name.includes(term) || email.includes(term) || dept.includes(term);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      showErrorToast("Please enter a meeting title");
      return;
    }
    if (!meetingDatetime) {
      showErrorToast("Please select meeting date and time");
      return;
    }

    const payload = {
      title: title.trim(),
      meeting_datetime: meetingDatetime,
      meeting_link: meetingLink ? meetingLink.trim() : null,
      participant_ids: selectedParticipantIds,
    };

    if (includeDocument && (documentTitle.trim() || documentContent.trim())) {
      payload.document = {
        title: documentTitle.trim() || `${title.trim()} - Notes`,
        content: documentContent,
      };
    }

    onSave(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: isDark ? "#111c44" : "#ffffff",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
          py: 2.5,
          px: 3,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: isDark ? "rgba(245, 158, 11, 0.15)" : "#fef3c7",
              color: "#d97706",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <VideoCallIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: isDark ? "#ffffff" : "#1b2559" }}>
              {isEdit ? "Edit Meeting" : "Schedule New Meeting"}
            </Typography>
            <Typography sx={{ color: isDark ? "#a3aed0" : "#707eae", fontSize: "0.8rem" }}>
              {isEdit ? "Update details, manage attendees, and revise notes" : "Set up meeting details and add team members"}
            </Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={onClose} sx={{ color: isDark ? "#a3aed0" : "#707eae" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
          {/* TITLE & DETAILS */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <Box sx={{ gridColumn: { xs: "span 1", sm: "span 2" } }}>
              <TextField
                label="Meeting Title"
                placeholder="e.g. Q4 Strategy Review & Roadmap Alignment"
                required
                fullWidth
                size="small"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                InputProps={{
                  sx: { borderRadius: "12px" },
                }}
              />
            </Box>

            <Box>
              <TextField
                label="Date & Time"
                type="datetime-local"
                required
                fullWidth
                size="small"
                value={meetingDatetime}
                onChange={(e) => setMeetingDatetime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  sx: { borderRadius: "12px" },
                  startAdornment: (
                    <InputAdornment position="start">
                      <EventIcon fontSize="small" sx={{ color: "#f59e0b" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box>
              <TextField
                label="Call Link (Google Meet / Zoom / Teams)"
                placeholder="https://meet.google.com/xyz-abcd-efg"
                fullWidth
                size="small"
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                InputProps={{
                  sx: { borderRadius: "12px" },
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon fontSize="small" sx={{ color: "#3b82f6" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0" }} />

          {/* ATTENDEES SELECTOR */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PeopleIcon fontSize="small" sx={{ color: "#f59e0b" }} />
                <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: isDark ? "#ffffff" : "#1b2559" }}>
                  Add People / Attendees
                </Typography>
                <Chip
                  label={`${selectedParticipantIds.length} Selected`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    bgcolor: selectedParticipantIds.length > 0 ? "rgba(245,158,11,0.15)" : (isDark ? "#1b2559" : "#f1f5f9"),
                    color: selectedParticipantIds.length > 0 ? "#b45309" : (isDark ? "#a3aed0" : "#64748b"),
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" onClick={handleSelectAll} sx={{ textTransform: "none", fontSize: "0.75rem", p: 0.5, color: "#f59e0b", fontWeight: 700 }}>
                  Select All
                </Button>
                <Typography sx={{ color: "text.disabled", fontSize: "0.75rem" }}>|</Typography>
                <Button size="small" color="inherit" onClick={handleClearAll} sx={{ textTransform: "none", fontSize: "0.75rem", p: 0.5 }}>
                  Clear
                </Button>
              </Stack>
            </Box>

            {/* Selected Chips */}
            {selectedParticipantIds.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  p: 1.5,
                  mb: 2,
                  borderRadius: "12px",
                  bgcolor: isDark ? "#0b1437" : "#f8fafc",
                  border: "1px solid",
                  borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                  maxHeight: 120,
                  overflowY: "auto",
                }}
              >
                {selectedParticipantIds.map((id) => {
                  const emp = employees.find((e) => e.id === id);
                  if (!emp) return null;
                  const name = `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || emp.email;
                  return (
                    <Chip
                      key={id}
                      avatar={<Avatar src={emp.profile_pic_url} alt={name}>{name.charAt(0)}</Avatar>}
                      label={name}
                      size="small"
                      onDelete={() => handleToggleParticipant(id)}
                      sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                    />
                  );
                })}
              </Box>
            )}

            {/* Search input */}
            <TextField
              placeholder="Search team members by name, email, or department..."
              size="small"
              fullWidth
              value={searchEmployee}
              onChange={(e) => setSearchEmployee(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: isDark ? "#a3aed0" : "#a0aec0" }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: "12px", fontSize: "0.85rem", mb: 1.5 },
              }}
            />

            {/* Employee checklist grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 1.5,
                maxHeight: 200,
                overflowY: "auto",
                p: 1,
                border: "1px solid",
                borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                borderRadius: "12px",
                bgcolor: isDark ? "#0b1437" : "#ffffff",
              }}
            >
              {filteredEmployees.length === 0 ? (
                <Typography sx={{ color: "text.secondary", p: 2, textAlign: "center", gridColumn: "span 2", fontSize: "0.85rem" }}>
                  No team members found matching search.
                </Typography>
              ) : (
                filteredEmployees.map((emp) => {
                  const isSelected = selectedParticipantIds.includes(emp.id);
                  const fullName = `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || emp.email;
                  return (
                    <Box
                      key={emp.id}
                      onClick={() => handleToggleParticipant(emp.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.25,
                        borderRadius: "10px",
                        border: "1px solid",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        bgcolor: isSelected
                          ? (isDark ? "rgba(245,158,11,0.12)" : "#fef3c7")
                          : (isDark ? "#111c44" : "#ffffff"),
                        borderColor: isSelected
                          ? "#f59e0b"
                          : (isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"),
                        "&:hover": {
                          borderColor: "#f59e0b",
                        },
                      }}
                    >
                      <Avatar
                        src={emp.profile_pic_url}
                        alt={fullName}
                        sx={{ width: 32, height: 32, fontSize: "0.75rem", bgcolor: "#f59e0b" }}
                      >
                        {fullName.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: isDark ? "#ffffff" : "#1b2559", noWrap: true }}>
                          {fullName}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: isDark ? "#a3aed0" : "#707eae", noWrap: true }}>
                          {emp.designation_name || emp.department_name || emp.email}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <CheckCircleIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>

          <Divider sx={{ borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0" }} />

          {/* MEETING DOCUMENT PREPARATION SECTION */}
          <Box
            sx={{
              p: 2.5,
              borderRadius: "16px",
              bgcolor: isDark ? "#0b1437" : "#faf5ff",
              border: "1px solid",
              borderColor: isDark ? "rgba(139, 92, 246, 0.2)" : "#e9d5ff",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    bgcolor: isDark ? "rgba(139, 92, 246, 0.2)" : "#ede9fe",
                    color: "#8b5cf6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DocumentIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: isDark ? "#ffffff" : "#4c1d95" }}>
                    Prepare Meeting Minutes & Agenda
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#c4b5fd" : "#6d28d9" }}>
                    Add agenda, discussion points, decisions, and action items
                  </Typography>
                </Box>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeDocument}
                    onChange={(e) => setIncludeDocument(e.target.checked)}
                    color="secondary"
                  />
                }
                label={includeDocument ? "Included" : "Add Notes"}
                sx={{ m: 0 }}
              />
            </Box>

            <Collapse in={includeDocument} sx={{ mt: 2 }}>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                  label="Document Title"
                  placeholder="e.g. Minutes of Meeting (MOM) - Sprint Review"
                  fullWidth
                  size="small"
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  InputProps={{ sx: { borderRadius: "10px" } }}
                />

                <Box>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.secondary", mb: 0.75 }}>
                    Meeting Notes & Agenda:
                  </Typography>
                  <HtmlEditor
                    value={documentContent}
                    onChange={(html) => setDocumentContent(html)}
                    placeholder="Document meeting goals, discussion points, decisions, and action items..."
                  />
                </Box>
              </Stack>
            </Collapse>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
            py: 2,
            px: 3,
            bgcolor: isDark ? "#0b1437" : "#f8fafc",
          }}
        >
          <Button onClick={onClose} color="inherit" sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <VideoCallIcon />}
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              color: "#ffffff",
              borderRadius: "10px",
              px: 3,
              py: 0.9,
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
              },
            }}
          >
            {isEdit ? "Update Meeting" : "Create Meeting"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
